import { useFeatureValue } from '@growthbook/growthbook-react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as Logo } from '../../assets/images/fi-logo.svg';
import { ReactComponent as HamburgerMenuIcon } from '../../assets/images/hamburger-menu.svg';
import { ReactComponent as ChatButton } from '../../assets/images/icons/chat.svg';
import { usePathBasedAppCustomization } from '../../hooks/usePathBasedAppCustomization';
import useShoppingCartBadgeItemCount from '../../hooks/useShoppingCartItemBadgeCount';
import { getDomainConfig } from '../../lib/domainConfig';
import { isMobileDevice } from '../../lib/util/isMobileDevice';
import styles from './AppBar.module.scss';
import ShoppingBag from './ShoppingBag';
import OrderConfirmedShoppingBag from './OrderConfirmedShoppingBag';
import MainNavigationV2 from './MainNavigationV2';
import { useTranslation } from 'react-i18next';

export function openDecagonChat() {
  // Select iframe and add visible class since entire chat widget is in a single iframe and button cannot be
  // hidden separately
  const chatIframe = document.querySelector('iframe[src*="decagon.ai"]');
  chatIframe?.classList.add('visible');
  // Only attempt open if duet is available (may not be available if script was blocked by browser)
  if ((window as any).duet && 'open' in (window as any).duet) {
    (window as any).duet.open();
  }
}

export function closeDecagonChat() {
  const chatIframe = document.querySelector('iframe[src*="decagon.ai"]');
  chatIframe?.classList.remove('visible');
}

export default function NavBar({
  navBarOpacity,
  postCheckout,
  hideMenu = false,
  logoPath,
}: {
  navBarOpacity?: number;
  postCheckout?: boolean;
  /** the redesigned cart keeps the logo and the bag and drops the menu, the easy exit */
  hideMenu?: boolean;
  /** an in-app route for the logo instead of the marketing site; the redesigned cart sends it to the shop */
  logoPath?: string;
}) {
  const { inCheckoutFlow, inBag } = usePathBasedAppCustomization();
  const [mainNavigationHidden, setMainNavigationHidden] = useState(true);
  const decagonChatEnabled = useFeatureValue('decagon-chat-widget', false);
  const { t } = useTranslation();

  const shoppingCartBadgeItemCount = useShoppingCartBadgeItemCount();

  const dismissMobileMenu = () => {
    // Hide nav
    setMainNavigationHidden(true);
  };

  const toggleMainNavigation = () => {
    setMainNavigationHidden(!mainNavigationHidden);
  };

  // don't fade/hide the navbar when the menu is open — otherwise scrolling
  // sets opacity to 0 and the menu disappears
  const effectiveNavBarOpacity = mainNavigationHidden ? navBarOpacity : undefined;

  const appBarClasses = classNames(styles.main, {
    [styles.open]: !mainNavigationHidden,
    // eslint-disable-next-line no-useless-computed-key
    ['nav-bar-open']: !mainNavigationHidden,
    [styles.checkout]: inCheckoutFlow,
  });

  const checkEscToDismissMenu = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !mainNavigationHidden) {
        dismissMobileMenu();
      }
    },
    [mainNavigationHidden],
  );

  // Display the chat button in place of bag if we are on the bag page or in checkout and on a mobile device
  const displayChatButtonInsteadOfBag = inBag && isMobileDevice();

  useEffect(() => {
    document.addEventListener('keydown', checkEscToDismissMenu, false);

    return () => {
      document.removeEventListener('keydown', checkEscToDismissMenu, false);
    };
  }, [checkEscToDismissMenu]);

  // lock body scroll when the menu is open so the page doesn't scroll behind it.
  // safari ignores overflow:hidden on body, so we use position:fixed instead.
  useEffect(() => {
    if (!mainNavigationHidden) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      // The Decagon chat iframe overlaps the lower half of the open menu and swallows
      // scroll events (menu scrolls up but not down). pointer-events:none isn't enough;
      // hide it entirely while the menu is open, restore on close.
      const chatIframe = document.querySelector<HTMLIFrameElement>('iframe[src*="decagon.ai"]');
      const prevChatDisplay = chatIframe?.style.display;
      if (chatIframe) chatIframe.style.display = 'none';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
        if (chatIframe) chatIframe.style.display = prevChatDisplay ?? '';
      };
    }
  }, [mainNavigationHidden]);

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (!displayChatButtonInsteadOfBag) return;
      if (event.origin !== 'https://decagon.ai') return;

      const { data } = event;
      if (data.type === 'USER_ACTION' && data.payload.eventName === 'DUET_DEINITIALIZE') {
        closeDecagonChat();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      // Close the chat when navigating away
      closeDecagonChat();
    };
  }, [displayChatButtonInsteadOfBag]);

  const handleChatClick = () => {
    if (decagonChatEnabled) {
      openDecagonChat();
    } else {
      // Only attempt open if ada is available (may not be available if script was blocked by browser)
      if ((window as any).adaEmbed && 'toggle' in (window as any).adaEmbed) {
        (window as any).adaEmbed.toggle();
      }
    }
  };

  return (
    <header
      className={classNames(appBarClasses, {
        'nav-bar-hidden': effectiveNavBarOpacity !== undefined && effectiveNavBarOpacity <= 0,
      })}
      style={
        effectiveNavBarOpacity !== undefined
          ? effectiveNavBarOpacity > 0
            ? { opacity: effectiveNavBarOpacity }
            : { opacity: effectiveNavBarOpacity, pointerEvents: 'none' }
          : {}
      }
    >
      <div className={classNames(styles.navBar, 'navBar')}>
        <div className={classNames(styles.leftNav, 'navBarIconContainer')}>
          {logoPath ? (
            <Link to={logoPath} aria-label="Fi">
              <Logo style={{ fill: 'black' }} />
            </Link>
          ) : (
            <a href={`https://${getDomainConfig().baseDomain}`} aria-label="Fi">
              <Logo style={{ fill: 'black' }} />
            </a>
          )}
        </div>
        <div className={classNames(styles.rightNav, 'navBarIconContainer')}>
          {displayChatButtonInsteadOfBag ? (
            <ChatButton className={styles.chatButton} onClick={handleChatClick}>
              {t('open_chat')}
            </ChatButton>
          ) : postCheckout ? (
            <OrderConfirmedShoppingBag />
          ) : (
            <ShoppingBag count={shoppingCartBadgeItemCount} />
          )}
          {!hideMenu && (
            <button
              type="button"
              aria-label={t('menu')}
              aria-expanded={!mainNavigationHidden}
              className={styles.iconButton}
              onClick={toggleMainNavigation}
            >
              <HamburgerMenuIcon className={styles.toggle} />
            </button>
          )}
        </div>
      </div>
      <div className={styles.overlay} onClick={dismissMobileMenu}></div>
      <MainNavigationV2 dismissMobileMenu={dismissMobileMenu} />
    </header>
  );
}
